import {
  CreateParams,
  CreateResult,
  DataProvider,
  GetListResult,
  GetManyParams,
  GetManyResult,
  GetOneParams,
  GetOneResult,
  Identifier,
  UpdateParams,
  UpdateResult,
} from 'react-admin'
import { NOTIFICATION_TAGS_URL } from '../../api-urls'
import { CacheableDataProviderExtensionResult } from '../../common/data-provider'
import {
  get,
  getWithPagination,
  patch,
  post,
  put,
} from '../../common/fetch.utils'
import {
  filterParamsComposer,
  queryParamsComposer,
} from '../../common/get-by-conditions.utils'
import { TicketAPIGetListParams } from '../../common/ticket-api-get-list.params'
import { NotificationTagDto } from '../../dto/system-notifications/notification-tag.dto'
import { mapSortTagsParam } from './notification-tags-sort.mapper'
import filterMapper from './notification-tags-filter.mapper'

const provider = {
  getList: async (
    resource: string,
    { filter, sort, pagination }: TicketAPIGetListParams,
  ): Promise<GetListResult<NotificationTagDto>> => {
    const filterParams = `o=>${filterParamsComposer('o', filter, filterMapper)}`
    const pathParams = queryParamsComposer(sort, pagination, mapSortTagsParam)
    const path = `/${filterParams}${(pathParams && `?${pathParams}`) || ''}`

    const {
      data,
      range: { total },
    } = await getWithPagination<NotificationTagDto[]>(
      `${NOTIFICATION_TAGS_URL}/GetByConditions`,
      path,
    )

    return Promise.resolve({
      data,
      total,
    })
  },
  getOne: async (
    resource: string,
    { id }: GetOneParams,
  ): Promise<GetOneResult<NotificationTagDto>> => {
    const data = await get<NotificationTagDto>(NOTIFICATION_TAGS_URL, `/${id}`)
    return Promise.resolve({
      data,
    })
  },
  getMany: async (
    resource: string,
    { ids }: GetManyParams,
  ): Promise<GetManyResult<NotificationTagDto>> => {
    const data = await get<NotificationTagDto[]>(
      NOTIFICATION_TAGS_URL,
      `/GetByConditions/e=>new int[] {${ids.toString()}}.Contains(e.Id)`,
    )
    return Promise.resolve({ data })
  },
  create: async (
    resource: string,
    { data }: CreateParams<CreateNotificationTagRequest>,
  ): Promise<CreateResult<NotificationTagDto>> => {
    const tag = await post<
      CreateNotificationTagRequest[],
      NotificationTagDto[]
    >(NOTIFICATION_TAGS_URL, [data])
    return {
      data: tag[0],
    }
  },
  update: async (
    resource: string,
    { id, data, previousData }: UpdateParams<NotificationTagDto>,
  ): Promise<UpdateResult> => {
    const update = await patch<
      { id: Identifier; [n: string]: any },
      NotificationTagDto
    >(NOTIFICATION_TAGS_URL, {
      id,
      description:
        data.description !== previousData.description
          ? data.description
          : undefined,
    })
    return Promise.resolve({ data: update })
  },
  attachCategories: async (
    resource: string,
    params: NotificationTagCategories,
  ): Promise<
    CacheableDataProviderExtensionResult<NotificationTagCategories>
  > => {
    const tagsCategories = await put<
      NotificationTagCategories,
      NotificationTagCategories
    >(`${NOTIFICATION_TAGS_URL}/AttachCategories`, params)
    return {
      data: tagsCategories,
    }
  },
  detachCategories: async (
    resource: string,
    params: NotificationTagCategories,
  ): Promise<
    CacheableDataProviderExtensionResult<NotificationTagCategories>
  > => {
    const tagsCategories = await put<
      NotificationTagCategories,
      NotificationTagCategories
    >(`${NOTIFICATION_TAGS_URL}/DetachCategories`, params)
    return {
      data: tagsCategories,
    }
  },
  attachUsers: async (
    resource: string,
    params: NotificationTagUsers,
  ): Promise<CacheableDataProviderExtensionResult<NotificationTagUsers>> => {
    const tagsUsers = await put<NotificationTagUsers, NotificationTagUsers>(
      `${NOTIFICATION_TAGS_URL}/AttachUsers`,
      params,
    )
    return {
      data: tagsUsers,
    }
  },
  detachUsers: async (
    resource: string,
    params: NotificationTagUsers,
  ): Promise<CacheableDataProviderExtensionResult<NotificationTagUsers>> => {
    const tagsUsers = await put<NotificationTagUsers, NotificationTagUsers>(
      `${NOTIFICATION_TAGS_URL}/DetachUsers`,
      params,
    )
    return {
      data: tagsUsers,
    }
  },
} as DetectorDataProvider

interface DetectorDataProvider extends DataProvider {
  attachCategories: (
    resource: string,
    params: NotificationTagCategories,
  ) => Promise<CacheableDataProviderExtensionResult<NotificationTagCategories>>

  detachCategories: (
    resource: string,
    params: NotificationTagCategories,
  ) => Promise<CacheableDataProviderExtensionResult<NotificationTagCategories>>

  attachUsers: (
    resource: string,
    params: NotificationTagUsers,
  ) => Promise<CacheableDataProviderExtensionResult<NotificationTagUsers>>

  detachUsers: (
    resource: string,
    params: NotificationTagUsers,
  ) => Promise<CacheableDataProviderExtensionResult<NotificationTagUsers>>
}

export interface CreateNotificationTagRequest {
  readonly description: string
}

interface NotificationTagCategories {
  notificationTagId: number
  categoriesIds: number[]
}

interface NotificationTagUsers {
  notificationTagId: number
  usersIds: number[]
}

export default provider
